import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { getVolunteerView, submitProof, getNextVolunteerTask } from '../api/client'

interface VolunteerData {
  scheduled_id: string
  media_type: 'image' | 'video'
  media_url: string
  caption: string
  hashtags: string[]
  instruction_text?: string
  scheduled_datetime: string
  proof_url?: string
  inbox_tracer?: string
  password_required: boolean
}

export default function Volunteer() {
  const { scheduledId } = useParams<{ scheduledId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [data, setData] = useState<VolunteerData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [proofUrl, setProofUrl] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  useEffect(() => {
    if (scheduledId) {
      loadData()
    }
  }, [scheduledId])

  async function loadData() {
    setLoading(true)
    setError(null)

    try {
      const response = await getVolunteerView(scheduledId!) as VolunteerData
      setData(response)
      if (response.proof_url) {
        setSubmitted(true)
        setProofUrl(response.proof_url)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load post')
    } finally {
      setLoading(false)
    }
  }

  async function handleSubmitProof(e: React.FormEvent) {
    e.preventDefault()
    if (!proofUrl.trim()) return

    setSubmitting(true)
    setError(null)

    try {
      const response = await submitProof(
        scheduledId!,
        proofUrl.trim(),
        data?.password_required ? password : undefined
      )
      setSubmitted(true)

      // If there's a next task, offer to navigate
      if (response.next_task) {
        const confirmNext = window.confirm(
          'Proof submitted! There are more tasks available. Go to next task?'
        )
        if (confirmNext) {
          navigate(`/volunteer/${response.next_task.scheduled_id}`)
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit proof')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleGoToNext() {
    if (!data?.inbox_tracer) return

    try {
      const response = await getNextVolunteerTask(data.inbox_tracer)
      if (response.has_next && response.scheduled_id) {
        navigate(`/volunteer/${response.scheduled_id}`)
      } else {
        alert('All tasks completed!')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get next task')
    }
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
  }

  function downloadMedia() {
    if (!data?.media_url) return
    const a = document.createElement('a')
    a.href = data.media_url
    a.download = `story-${scheduledId}.${data.media_type === 'video' ? 'mp4' : 'png'}`
    a.click()
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Post not found'}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-indigo-600 hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
            <h1 className="text-xl font-bold">Instagram Story Task</h1>
            <p className="text-indigo-100 text-sm mt-1">
              Scheduled: {new Date(data.scheduled_datetime).toLocaleString()}
            </p>
          </div>

          {/* Media Preview */}
          <div className="aspect-[9/16] bg-black relative">
            {data.media_type === 'video' ? (
              <video
                src={data.media_url}
                controls
                className="w-full h-full object-contain"
              />
            ) : (
              <img
                src={data.media_url}
                alt="Story"
                className="w-full h-full object-contain"
              />
            )}
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Instructions */}
            {data.instruction_text && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-medium text-yellow-800 mb-2">Instructions</h3>
                <p className="text-sm text-yellow-700 whitespace-pre-wrap">
                  {data.instruction_text}
                </p>
              </div>
            )}

            {/* Caption */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium text-gray-900">Caption</h3>
                <button
                  onClick={() => copyToClipboard(data.caption, 'caption')}
                  className="text-sm text-indigo-600 hover:text-indigo-700"
                >
                  {copied === 'caption' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                {data.caption}
              </p>
            </div>

            {/* Hashtags */}
            {data.hashtags.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-gray-900">Hashtags</h3>
                  <button
                    onClick={() =>
                      copyToClipboard(data.hashtags.map((t) => `#${t}`).join(' '), 'hashtags')
                    }
                    className="text-sm text-indigo-600 hover:text-indigo-700"
                  >
                    {copied === 'hashtags' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.hashtags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-indigo-50 text-indigo-600 text-sm rounded"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Download Button */}
            <button
              onClick={downloadMedia}
              className="w-full py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              Download {data.media_type === 'video' ? 'Video' : 'Image'}
            </button>

            {/* Post ID */}
            <div className="flex justify-between items-center text-sm text-gray-500">
              <span>Post ID: {scheduledId?.slice(0, 8)}</span>
              <button
                onClick={() => copyToClipboard(scheduledId!, 'id')}
                className="text-indigo-600 hover:text-indigo-700"
              >
                {copied === 'id' ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Proof Submission */}
          <div className="p-6 border-t border-gray-200">
            {submitted ? (
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-green-600 font-medium">Proof Submitted!</p>
                <p className="text-sm text-gray-500 mt-1 break-all">{proofUrl}</p>

                {data.inbox_tracer && (
                  <button
                    onClick={handleGoToNext}
                    className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Go to Next Task
                  </button>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmitProof} className="space-y-4">
                <h3 className="font-medium text-gray-900">Submit Proof of Posting</h3>
                <p className="text-sm text-gray-600">
                  After you've posted the story, paste the link to your story below.
                </p>

                {data.password_required && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Story URL
                  </label>
                  <input
                    type="url"
                    value={proofUrl}
                    onChange={(e) => setProofUrl(e.target.value)}
                    placeholder="https://instagram.com/stories/..."
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-600">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting || !proofUrl.trim()}
                  className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {submitting ? 'Submitting...' : 'Submit Proof'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
